import type { KeyboardEvent } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFragment } from '@frameio/graphql-client';
import type {
  Project,
  ProjectSettingsFieldsFragment,
  Workspace,
} from '@frameio/graphql-client/generated';
import {
  ProjectSettingsFields,
  WorkspaceInfoBasic,
} from '@frameio/graphql-client/generated';
import { useWorkspacePermissions } from '@frameio/permissions';
import { useDuplicateProject } from '@frameio/shared/resources';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogFooter,
  DialogHeader,
  Text,
  theme,
  ToggleCheckbox,
  Tooltip,
  useActiveWindowBreakpoint,
  useMeasure,
} from '@frameio/vapor';
import TEXT from './generated/TEXT/DuplicateProjectModal';
import { ProjectAccessSwitch } from './ProjectAccessSwitch';
import { ProjectAvatarEditor } from './ProjectAvatarEditor';
import {
  ContentColumn,
  ContentContainer,
  IncludeAssetsWrapper,
} from './styles';
import { WorkspacePicker } from './WorkspacePicker';

const INCLUDE_ASSETS_LIMIT = 500;
const FOCUS_TIMEOUT_DURATION = 50;
const INCLUDE_ASSETS_TOGGLE_ID = 'duplicate-project-include-assets-toggle';

export type DuplicateProjectModalProps = {
  accountId: string;
  onRequestClose: () => void;
  isShowing: boolean;
  projectId: Project['id'];
  workspaceId: Workspace['id'];
};

export const screenVariants = {
  base: {
    dialogWidth: '100%',
    pickerWidth: '100%',
  },
  tablet: {
    dialogWidth: 620,
    pickerWidth: 280,
  },
  desktop: {
    dialogWidth: 620,
    pickerWidth: 280,
  },
};

export const DuplicateProjectModal = ({
  accountId,
  isShowing,
  onRequestClose,
  projectId,
  workspaceId,
}: DuplicateProjectModalProps) => {
  const { data: project } = useFragment<
    ProjectSettingsFieldsFragment,
    undefined
  >({
    fragment: ProjectSettingsFields,
    fragmentName: 'ProjectSettingsFields',
    from: {
      __typename: 'Project',
      id: projectId ?? '',
    },
  });

  const { data: selectedWorkspace } = useFragment({
    fragment: WorkspaceInfoBasic,
    fragmentName: 'WorkspaceInfoBasic',
    from: {
      __typename: 'Workspace',
      id: workspaceId ?? '',
    },
  });

  const [name, setName] = useState(
    project?.name ? (TEXT.defaultProjectName(project?.name) as string) : ''
  );
  const [isRestricted, setIsRestricted] = useState(project?.private ?? false);

  const handleToggleIsRestricted = useCallback(
    () => setIsRestricted((prev) => !prev),
    []
  );

  const [shouldIncludeAssets, setShouldIncludeAssets] = useState(true);
  const [avatarBlob, setAvatarBlob] = useState<Blob | null>(null);
  const [deleteAvatar, setDeleteAvatar] = useState(false);
  const nameInputRef = useRef<HTMLInputElement>(null);

  // hold onto the thumb so we can keep the image while the dialog closes
  const avatarThumbRef = useRef<string | null>(null);
  const isOverAssetLimit =
    !!project?.fileCount && project.fileCount > INCLUDE_ASSETS_LIMIT;

  /**
   * This effect updates the local state when the project changes.
   */
  const { defaultProjectName } = TEXT;
  const projectNameText = project?.name
    ? (defaultProjectName(project.name) as string)
    : '';

  useEffect(() => {
    if (project?.name) {
      setName(projectNameText);
    }
    if (project?.images?.image256) {
      avatarThumbRef.current = project.images.image256;
    }
  }, [projectId, project?.images?.image256, project?.name, projectNameText]);

  const duplicateProject = useDuplicateProject();
  const breakpoint = useActiveWindowBreakpoint();
  const [ref, bounds] = useMeasure();
  const inputMeasure =
    breakpoint === 'base'
      ? bounds.width
      : screenVariants[breakpoint].pickerWidth;

  const avatarThumb = avatarThumbRef.current ?? project?.images?.image256;
  const avatarOriginal = deleteAvatar
    ? null
    : avatarThumb ?? project?.images?.original;

  const handleAvatarSave = (blob: Blob) => {
    setAvatarBlob(blob);
    avatarThumbRef.current = blob ? URL.createObjectURL(blob) : null;
    if (project?.images?.original && blob === null) {
      setDeleteAvatar(true);
    } else {
      setDeleteAvatar(false);
    }
  };

  const onCreateClick = () => {
    duplicateProject(
      {
        name,
        destinationWorkspaceId: workspaceId,
        sourceProjectId: projectId,
        assetsToCopy:
          shouldIncludeAssets && !isOverAssetLimit ? 'ALL' : 'FOLDERS_ONLY',
        private: isRestricted,
      },
      accountId,
      avatarBlob,
      deleteAvatar
    );
    onRequestClose();
  };

  const handleDialogExited = () => {
    setName('');
    setDeleteAvatar(false);
    setAvatarBlob(null);
    avatarThumbRef.current = null;
  };

  const [handleIncludeAssetsToggleClick, handleIncludeAssetsKeyDown] =
    useMemo(() => {
      const toggleIncludeAssets = () =>
        setShouldIncludeAssets(!shouldIncludeAssets);
      return [
        toggleIncludeAssets,
        (event: KeyboardEvent<HTMLDivElement>) => {
          if (event.key === 'Enter' || event.key === ' ') {
            toggleIncludeAssets();
          }
        },
        [shouldIncludeAssets],
      ];
    }, [shouldIncludeAssets]);

  const isSaveDisabled = name.trim() === '';

  useEffect(() => {
    if (isShowing && nameInputRef.current) {
      setTimeout(() => {
        nameInputRef.current?.focus();
        nameInputRef.current?.select();
      }, FOCUS_TIMEOUT_DURATION);
    }
  }, [isShowing]);

  const canEditUserPermissions = useWorkspacePermissions({
    check: 'canEditUserPermissions',
    workspaceId,
  });

  return (
    <Dialog
      aria-labelledby={TEXT.duplicateProject}
      enableClickOutsideToClose={false}
      initialFocusRef={nameInputRef}
      isShowing={isShowing}
      onExited={handleDialogExited}
      onRequestClose={onRequestClose}
      style={{
        minWidth: screenVariants[breakpoint]?.dialogWidth,
        display: 'flex',
        flexDirection: 'column',
        maxHeight: '600px',
        overflow: 'hidden',
      }}
    >
      <DialogHeader
        closeLabel={TEXT.dialogCloseLabel}
        subtitle={TEXT.subtitle}
        title={TEXT.duplicateProject}
      />
      <Box
        height="100%"
        style={{
          overflowY: 'scroll',
        }}
      >
        <ContentContainer
          desktopPickerWidth={screenVariants.desktop.pickerWidth}
          tabletPickerWidth={screenVariants.tablet.pickerWidth}
        >
          <ContentColumn>
            <ProjectAvatarEditor
              avatarOriginal={avatarOriginal}
              avatarThumb={avatarThumb}
              isRestricted={isRestricted}
              name={name}
              nameInputDataTestId="workspaces-duplicate-project-modal--name-field"
              onNameChange={setName}
              onSave={handleAvatarSave}
              shouldFocusOnMount={true}
            />
          </ContentColumn>
          <ContentColumn ref={ref} grow={true}>
            {canEditUserPermissions && (
              <ProjectAccessSwitch
                isRestricted={isRestricted}
                onChange={handleToggleIsRestricted}
              />
            )}

            <WorkspacePicker
              isDisabled={true}
              selectedWorkspace={selectedWorkspace}
              width={inputMeasure}
            />
          </ContentColumn>
        </ContentContainer>
      </Box>

      <DialogFooter>
        <Tooltip
          disabled={!isOverAssetLimit}
          maxWidth={'360px'}
          title={TEXT.overAssetLimit}
        >
          <IncludeAssetsWrapper $disabled={isOverAssetLimit}>
            <ToggleCheckbox
              checked={shouldIncludeAssets && !isOverAssetLimit}
              onKeyDown={handleIncludeAssetsKeyDown}
              onMouseDown={handleIncludeAssetsToggleClick}
              style={{ flexShrink: 0 }}
              // Workaround for disabled state showing regardless to prop value
              {...(isOverAssetLimit ? { disabled: true } : {})}
              aria-checked={shouldIncludeAssets && !isOverAssetLimit}
              aria-labelledby={INCLUDE_ASSETS_TOGGLE_ID}
              role="checkbox"
              tabIndex={0}
            />
            <Text
              color="body"
              id={INCLUDE_ASSETS_TOGGLE_ID}
              style={{ paddingLeft: theme.spaces[8] }}
              variant="body"
            >
              {TEXT.includeAssets}
            </Text>
          </IncludeAssetsWrapper>
        </Tooltip>
        <DialogActions>
          <Button
            data-testid="workspaces-duplicate-project-modal--cancel-button"
            onClick={onRequestClose}
          >
            {TEXT.cancel}
          </Button>
          <Button
            color="primary"
            data-testid="workspaces-duplicate-project-modal--save-button"
            disabled={isSaveDisabled}
            onClick={onCreateClick}
          >
            {TEXT.create}
          </Button>
        </DialogActions>
      </DialogFooter>
    </Dialog>
  );
};

TEXT.DEFINE(
  {
    /** Placeholder text for project name input */
    addProjectName: 'Add project name',
    /** Secondary action label to cancel duplication */
    cancel: 'Cancel',
    /** Primary action label to create the duplicate */
    create: 'Duplicate Project',
    /** Default name for a duplicated project */
    defaultProjectName: (name: string) => `${name} copy`,
    /** Dialog title for duplicating a project */
    duplicateProject: 'Duplicate Project',
    /** Label for the avatar editor action */
    editAvatar: 'Edit project avatar',
    /** Toggle label for including assets in the duplicate */
    includeAssets: 'Include assets',
    /** Field label for project name */
    name: 'Name',
    /** Toast or inline message for discarded changes */
    changesDiscarded: 'Changes have been discarded',
    /** Dialog title for creating a new project */
    createProject: 'Create New Project',
    /** Subtitle describing what is excluded from duplication */
    subtitle: 'Does not include users, comments, or Shares',
    /** Button label to finish the dialog */
    done: 'Done',
    /** Validation message for name length limits */
    tooLong: (number: number) =>
      `You’ve exceeded the ${number} character limit for this name`,
    /** Generic error message for failed actions */
    tryAgain: 'Something went wrong, please try again.',
    /** Warning when project exceeds asset duplication limit */
    overAssetLimit: `Cannot duplicate assets in projects with over ${INCLUDE_ASSETS_LIMIT} assets`,
    /** Accessible label for the dialog close button */
    dialogCloseLabel: 'Close Dialog',
  },
  { INCLUDE_ASSETS_LIMIT }
);
